import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { type CapacityFormData, isInfinityLimit } from "../types";

export type BasicLimits = Pick<CapacityFormData, "concurrentMax" | "dailyMax" | "durationAware">;

/** Rendered only while confirming, so it is always open. */
interface BasicLimitsConfirmDialogProps {
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  /** Limits captured when Save was pressed. */
  limits: BasicLimits;
}

export function BasicLimitsConfirmDialog({
  onClose,
  onConfirm,
  limits,
}: BasicLimitsConfirmDialogProps) {
  const { t } = useTranslation("settings");
  const [saving, setSaving] = useState(false);

  const formatLimit = (value: number) =>
    isInfinityLimit(value) ? t("capacity.infinity") : String(value);

  const handleConfirm = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      await onConfirm();
    } finally {
      setSaving(false);
      onClose();
    }
  };

  return (
    <AlertDialog open onOpenChange={(open) => !open && !saving && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t("capacity.confirmSave.title")}</AlertDialogTitle>
          <AlertDialogDescription>{t("capacity.confirmSave.description")}</AlertDialogDescription>
        </AlertDialogHeader>
        <dl className="space-y-2 rounded-lg bg-muted p-4 text-sm">
          <div className="flex items-center justify-between gap-4">
            <dt className="text-muted-foreground">
              {limits.durationAware
                ? t("capacity.maxConcurrentAppointments")
                : t("capacity.maxAppointmentsPerStartHour")}
            </dt>
            <dd className="font-semibold">{formatLimit(limits.concurrentMax)}</dd>
          </div>
          <div className="flex items-center justify-between gap-4">
            <dt className="text-muted-foreground">{t("capacity.maxDailyAppointments")}</dt>
            <dd className="font-semibold">{formatLimit(limits.dailyMax)}</dd>
          </div>
        </dl>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={saving}>{t("actions.cancel")}</AlertDialogCancel>
          <AlertDialogAction onClick={handleConfirm} disabled={saving}>
            {t("actions.save")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
